<template>
  <InputWrapper
    v-slot="{ ariaProps, inputClasses, inputId, invalid }"
    :input-id="id"
    :label="locationInputLabel"
    :errors="v$.id.$errors"
  >
    <LocationQuickSearch
      :id="inputId"
      v-bind="ariaProps"
      v-model="internalLocation"
      :shipping-point-types="[ShippingPointType.CROSS_DOCK]"
      :class="inputClasses"
      :append-to="appendTo"
      :invalid="invalid"
      :autofocus="autofocus"
      :placeholder="locationInputLabel"
      @clear="onClear"
      @keypress.enter="onEnterInDropdownInput"
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import { Location, Optional, ShippingPointType } from '@rygen/rygen-ui/model';

import { TRANSLATIONS_BY_STOP_SELECTOR } from '@/model/routeBuilder';

import LocationQuickSearch from '@/components/location/LocationQuickSearch.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  location: {
    type: Object as PropType<Location>,
    default: () => undefined,
  },
  locationLabel: {
    type: Function as PropType<(defaultLabel: string) => string>,
    default: () => undefined,
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  autofocus: {
    type: Boolean,
    default: () => false,
  },
});
const { id, location, locationLabel, appendTo, autofocus } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:location', newVal: Location): void;
}>();

const { t } = useI18n();

const internalLocation: Ref<Optional<Location>> = ref();

const locationInputLabel = computed(() => {
  const base = t('common.newParam', {
    param: TRANSLATIONS_BY_STOP_SELECTOR.crossdock,
  });
  return locationLabel?.value?.(base) ?? base;
});

// vuelidate can't handle an undefined model so set a default
const validationModel = computed(
  () => internalLocation.value ?? new Location(),
);

const rules = computed(() => ({
  id: {
    required: required(locationInputLabel.value),
  },
}));
const { v$ } = useValidations(validationModel, rules);

const onEnterInDropdownInput = (event: KeyboardEvent) => {
  // Stop the keyboard event from bubbling up to the popup card so that the
  // popup isn't closed.
  event.stopPropagation();
};

const onClear = () => {
  internalLocation.value = undefined;
  v$.value.id.$reset();
};

watch(
  () => location?.value,
  (newVal) => {
    internalLocation.value = newVal ? new Location(newVal) : undefined;
  },
  { deep: true, immediate: true },
);

watch(
  () => internalLocation.value?.id,
  (newVal) => {
    if (newVal && newVal !== location?.value?.id) {
      // The condition above guarantees `internalLocation` is defined
      emit('update:location', internalLocation.value!);
    }
  },
);
</script>
